import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { get, isEmpty, upperCase } from 'lodash';

import { OrgSubUnit } from '@appTypes/esb';
import {
  deleteSubUnitResponse,
  editSubUnitSuccess,
  getSubUnitsError,
  getSubUnitsResponse,
  saveSubUnitSuccess,
} from '@modules/advancement/subUnits';
import { denTypesNames } from '@shared/constants';
import { SET_ORGANIZATION } from '@shared/duck/actions';

import { UNASSIGNED, defaultFilter } from '../constants';
import {
  PackRosterFilters,
  PackRosterSorter,
  RosterSlice,
  SubUnitFilterTag,
} from '../types';
import { formatSubUnitFilters, retrieveStoredFilters } from '../utilsTyped';

const initialState: RosterSlice = {
  groupByOrderStatus: false,
  rowGroupExpanded: {},
  search: '',
  filter: defaultFilter,
  sorter: {
    columnKey: 'name',
    order: 'ascend',
  },
  showSubUnits: false,
  subUnitFilterTags: [],
};

export const rosterSlice = createSlice({
  name: 'rosterSlice',
  initialState: initialState,
  extraReducers: builder => {
    builder
      .addCase(
        getSubUnitsResponse.type,
        (state, action: PayloadAction<{ subUnits: OrgSubUnit[] }>) => {
          if (!isEmpty(state.subUnitFilterTags)) {
            return;
          }

          const filters = action.payload?.subUnits
            .filter(subUnit => subUnit.isApproved)
            .map(subUnit => {
              const isAol = subUnit.denType === denTypesNames.aol;
              const denType = subUnit.denType
                ? `${isAol ? upperCase(subUnit.denType) : subUnit.denType} `
                : '';
              return {
                id: subUnit.subUnitId,
                name: `${denType}${subUnit.subUnitName}`,
                status: true,
                denType: subUnit.denType,
              };
            });

          state.subUnitFilterTags = [
            ...filters,
            {
              id: -1,
              name: UNASSIGNED,
              status: true,
            },
          ];
        },
      )
      .addCase(getSubUnitsError.type, state => {
        state.subUnitFilterTags = [];
      })
      .addCase(
        saveSubUnitSuccess.type,
        (
          state,
          action: PayloadAction<{ subUnit: OrgSubUnit; orgGuid: string }>,
        ) => {
          const newSubUnit = action.payload?.subUnit;
          const newSubUnitFilter = formatSubUnitFilters(newSubUnit);
          state.subUnitFilterTags = [
            ...state.subUnitFilterTags,
            newSubUnitFilter,
          ];
        },
      )
      .addCase(
        editSubUnitSuccess.type,
        (
          state,
          action: PayloadAction<{ subUnit: OrgSubUnit; orgGuid: string }>,
        ) => {
          const editedSubUnit = action.payload.subUnit;
          const editedSubUnitFilter = formatSubUnitFilters(editedSubUnit);
          const editedSubUnitsFilter = state.subUnitFilterTags.reduce(
            (curr, subUnitFilter) => {
              if (subUnitFilter.id === editedSubUnitFilter.id) {
                return [...curr, editedSubUnitFilter];
              }
              return [...curr, subUnitFilter];
            },
            [],
          );
          state.subUnitFilterTags = editedSubUnitsFilter;
        },
      )
      .addCase(
        deleteSubUnitResponse.type,
        (
          state,
          action: PayloadAction<{ orgGuid: string; subUnitId: number }>,
        ) => {
          const deletedSubUnit = action.payload.subUnitId;
          const updatedSubUnitsFilter = state.subUnitFilterTags.filter(
            tag => tag.id !== deletedSubUnit,
          );
          state.subUnitFilterTags = updatedSubUnitsFilter;
        },
      )
      .addMatcher(
        (action): action is PayloadAction<{ guid: string }> =>
          action.type === SET_ORGANIZATION,
        (state, action) => {
          // Change to be by orgGuid
          const storedFilters = retrieveStoredFilters();
          const storedFiltersPerGuid = get(
            storedFilters,
            action.payload.guid,
            null,
          );
          if (storedFiltersPerGuid) {
            state.filter = storedFiltersPerGuid.filter;
            state.sorter = storedFiltersPerGuid.sorter;
            state.showSubUnits = storedFiltersPerGuid.showSubUnits;
            state.subUnitFilterTags = storedFiltersPerGuid.subUnitFilterTags;
            return;
          }
          // reset state when changing unit
          state.filter = defaultFilter;
          state.subUnitFilterTags = [];
        },
      );
  },
  reducers: {
    toggleGroupByOrderStatus: state => {
      state.groupByOrderStatus = !state.groupByOrderStatus;
    },
    toggleExpandGroup: (
      state,
      action: PayloadAction<{
        groupId: string;
        toggle: boolean;
        type: 'order-status';
      }>,
    ) => {
      const { groupId, toggle, type } = action.payload;
      const key = `${type}:${groupId}`;
      state.rowGroupExpanded[key] = toggle;
    },
    setPackRosterSearch: (
      state,
      action: PayloadAction<{
        search: string;
      }>,
    ) => {
      const { search } = action.payload;
      state.search = search;
    },
    setPackRosterFilter: (
      state,
      action: PayloadAction<{ filter: PackRosterFilters }>,
    ) => {
      const { filter } = action.payload;
      state.filter = filter;
    },
    setPackRosterSorter: (
      state,
      action: PayloadAction<{ sorter: PackRosterSorter }>,
    ) => {
      const { columnKey, order } = action.payload.sorter;
      const newSorter = {
        columnKey,
        order,
      };
      state.sorter = newSorter;
    },
    setShowSubUnits: (state, action: PayloadAction<{ show: boolean }>) => {
      const { show } = action.payload;
      state.showSubUnits = show;
    },
    setSubUnitFilterTags: (
      state,
      action: PayloadAction<{ tags: SubUnitFilterTag[] }>,
    ) => {
      const { tags } = action.payload;
      state.subUnitFilterTags = tags;
    },
  },
});

const { actions, reducer } = rosterSlice;

export { reducer };

export const {
  toggleGroupByOrderStatus,
  toggleExpandGroup,
  setPackRosterSearch,
  setPackRosterFilter,
  setPackRosterSorter,
  setShowSubUnits,
  setSubUnitFilterTags,
} = actions;
